import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '../../store/authStore';
import { companyService } from '../../services/companyService';
import { Upload, X } from 'lucide-react';
import { Button } from '../common/Button';
import { useSettingsStore } from '../../store/settingsStore';
import { useCompanyLogo } from '../../hooks/useCompanyLogo';
import { useQueryClient } from '@tanstack/react-query';
import { useLanguage } from '../../features/language/useLanguage';
import { cn } from '../../utils/cn';

const companySchema = z.object({
  name: z.string().min(1, 'Company Name is required'),
  address: z.string().optional(),
  email: z.string().email('Invalid email address').optional().or(z.literal('')),
  phone: z.string().optional(),
  website: z.string().url('Invalid URL').optional().or(z.literal('')),
  currency_symbol: z.string().optional(),
});

type CompanyFormData = z.infer<typeof companySchema>;

interface CompanyProfileFormProps {
  readOnly?: boolean;
}

export const CompanyProfileForm: React.FC<CompanyProfileFormProps> = ({ readOnly = false }) => {
  const { t } = useLanguage();
  const { company, setCompany } = useAuthStore();
  const { setCurrencySymbol } = useSettingsStore();
  const queryClient = useQueryClient();
  const [isSuccess, setIsSuccess] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const [logoUrl, setLogoUrl] = React.useState<string | null>(null);
  const { data: fetchedLogoUrl, isPending: isLogoLoading } = useCompanyLogo();
  const [isUploadingLogo, setIsUploadingLogo] = React.useState(false);
  const [logoError, setLogoError] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    // Sync React Query data to local state for rendering
    if (fetchedLogoUrl !== undefined) {
      setLogoUrl(fetchedLogoUrl);
    }
  }, [fetchedLogoUrl]);

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<CompanyFormData>({
    resolver: zodResolver(companySchema),
    defaultValues: {
      name: '',
      address: '',
      email: '',
      phone: '',
      website: '',
      currency_symbol: '',
    }
  });

  useEffect(() => {
    if (company) {
      reset({
        name: company.name,
        address: company.address || '',
        email: company.email || '',
        phone: company.phone || '',
        website: company.website || '',
        currency_symbol: company.currency_symbol || '',
      });
    }
  }, [company, reset]);

  const onSubmit = async (data: CompanyFormData) => {
    setError(null);
    setIsSuccess(false);
    try {
      const updatedCompany = await companyService.updateProfile(data);
      setCompany(updatedCompany);
      
      // Update local settings store if currency symbol changed
      if (data.currency_symbol) {
        setCurrencySymbol(data.currency_symbol);
      }
      
      setIsSuccess(true);
      setTimeout(() => setIsSuccess(false), 3000);
    } catch (err) {
      setError(t('settingsCompany.errors.updateFailed'));
      console.error(err);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLogoError(null);
    // Validate size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setLogoError(t('settingsCompany.errors.logoMaxSize'));
      return;
    }

    // Validate type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setLogoError(t('settingsCompany.errors.logoType'));
      return;
    }

    setIsUploadingLogo(true);
    try {
      await companyService.uploadLogo(file);
      await queryClient.invalidateQueries({ queryKey: ['companyLogo'] });
    } catch (err: unknown) {
      setLogoError(t('settingsCompany.errors.uploadFailed'));
      console.error(err);
    } finally {
      setIsUploadingLogo(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleLogoDelete = async () => {
    setLogoError(null);
    try {
      await companyService.deleteLogo();
      await queryClient.invalidateQueries({ queryKey: ['companyLogo'] });
    } catch (err: unknown) {
      setLogoError(t('settingsCompany.errors.deleteFailed'));
      console.error(err);
    }
  };

  return (
    <div className="bg-white shadow sm:rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg leading-6 font-medium text-gray-900">{t('settingsCompany.title')}</h3>
        <div className="mt-2 max-w-xl text-sm text-gray-500">
          <p>{t('settingsCompany.subtitle')}</p>
        </div>

        {/* Logo Section */}
        <div className="mt-6 sm:mt-5 flex flex-col gap-3 sm:border-t sm:border-gray-200 sm:pt-5">
          <label className="block text-sm font-medium text-gray-700">
            {t('settingsCompany.logo')}
          </label>

          <div className="mt-1 sm:mt-0">
            <div className="flex flex-col gap-3 items-start">
              <input
                type="file"
                ref={fileInputRef}
                accept="image/png, image/jpeg, image/jpg, image/webp"
                className="hidden"
                onChange={handleLogoUpload}
              />
              <div
                className={cn(
                  "relative group flex items-center justify-center bg-gray-50 border border-gray-200 border-dashed rounded-md min-w-[6rem] min-h-[6rem] p-4 transition-colors",
                  !readOnly && "cursor-pointer hover:bg-gray-100"
                )}
                onClick={() => !readOnly && fileInputRef.current?.click()}
              >
                {isLogoLoading || isUploadingLogo ? (
                  <span className="text-xs text-gray-500 font-medium max-w-[12rem] text-center">{isUploadingLogo ? t('inventory.lotDetail.uploading') : t('inventory.lotDetail.loadingShort')}</span>
                ) : logoUrl ? (
                  <>
                    <img
                      src={logoUrl}
                      alt={t('settingsCompany.logo')}
                      className="max-h-24 max-w-[12rem] object-contain group-hover:opacity-75 transition-opacity"
                    />
                    {!readOnly && (
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/10 rounded-md">
                        <div className="bg-white text-gray-700 px-3 py-1 rounded-full text-xs font-medium shadow-sm flex items-center gap-1">
                          <Upload className="h-3 w-3" /> {t('inventory.lotDetail.actions.changePhoto')}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className={cn(
                    "flex flex-col items-center gap-2 text-gray-400 transition-colors",
                    !readOnly && "group-hover:text-primary-500"
                  )}>
                    <Upload className="h-6 w-6" />
                    <span className="text-xs font-medium text-center px-2">{readOnly ? t('settingsMcp.values.none') : t('inventory.lotDetail.actions.clickUpload')}</span>
                  </div>
                )}
                
                {logoUrl && !isLogoLoading && !isUploadingLogo && !readOnly && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLogoDelete();
                    }}
                    className="absolute -top-3 -right-3 bg-red-100 bg-opacity-90 hover:bg-red-500 hover:text-white text-red-600 rounded-full p-1.5 shadow-sm transition-colors border border-red-200"
                    title={t('settingsCompany.removeLogo')}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              
              <div className="flex flex-col gap-1">
                {logoError && <p className="text-sm text-red-600">{logoError}</p>}
                <p className="text-xs text-gray-500">{t('inventory.lotDetail.uploadHint')}</p>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-6 sm:border-t sm:border-gray-200 sm:pt-6">
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            
            {/* Company Name */}
            <div className="sm:col-span-6">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                {t('settingsCompany.companyName')}
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  id="name"
                  data-test-id="settings-company-name"
                  {...register('name')}
                  disabled={readOnly}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 disabled:bg-gray-50 disabled:text-gray-500"
                />
                {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
              </div>
            </div>

            {/* Address */}
            <div className="sm:col-span-6">
              <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                {t('settingsCompany.address')}
              </label>
              <div className="mt-1">
                <textarea
                  id="address"
                  rows={3}
                  data-test-id="settings-company-address"
                  {...register('address')}
                  disabled={readOnly}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
            </div>

            {/* Email */}
            <div className="sm:col-span-3">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                {t('forms.common.email')}
              </label>
              <div className="mt-1">
                <input
                  type="email"
                  id="email"
                  data-test-id="settings-company-email"
                  {...register('email')}
                  disabled={readOnly}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 disabled:bg-gray-50 disabled:text-gray-500"
                />
                {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
              </div>
            </div>

            {/* Phone */}
            <div className="sm:col-span-3">
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                {t('forms.common.phone')}
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  id="phone"
                  data-test-id="settings-company-phone"
                  {...register('phone')}
                  disabled={readOnly}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
            </div>

            {/* Website */}
            <div className="sm:col-span-3">
              <label htmlFor="website" className="block text-sm font-medium text-gray-700">
                {t('settingsCompany.website')}
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  id="website"
                  data-test-id="settings-company-website"
                  {...register('website')}
                  disabled={readOnly}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 disabled:bg-gray-50 disabled:text-gray-500"
                  placeholder={t('settingsCompany.websitePlaceholder')}
                />
                {errors.website && <p className="mt-1 text-sm text-red-600">{errors.website.message}</p>}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-5">
            {isSuccess && <span className="text-sm text-green-600">{t('settingsCompany.savedSuccess')}</span>}
            {error && <span className="text-sm text-red-600">{error}</span>}
            {!readOnly && (
              <Button
                type="submit"
                data-test-id="settings-company-submit"
                isLoading={isSubmitting}
              >
                {t('inventory.modal.actions.saveChanges')}
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};
